import * as PIXI from 'pixi.js';

import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import pick from 'lodash/pick';
import omit from 'lodash/omit';

import type { SceneImageRenderMode } from '../../enums';
import { SeekMode, BufferingMode, AssetCreatedFrom, AssetSuffix } from '../../enums';
import type { RawSceneData, OpaqueVideoData, Version, RawAssetData } from '../../video-data/types';
import type {
    Asset,
    AssetType,
    GetAssetType,
    AssetName,
    GetAssetTypeByName,
    GetAssetsOptions,
    BaseAssetSettings,
    BaseAsset,
} from '../asset/BaseAsset';
import { Migrator, VERSION } from '../../video-data/migrations/migrator';
import uuid, { isUuid } from '../../video-data/utils/uuid';
import type { SceneSfxItem, AddSceneSfxSettings, SfxOwner } from '../../audio/sfx-types';
import { SceneSfxEvent } from '../../audio/sfx-types';
import { AudioAsset } from '../../audio/AudioAsset';
import { Config } from '../../video-data/config';

import {
    assertHasOwnProperties,
    assertInArray,
    assertMinimumLength,
    assertNotInArray,
    assertNumberBetween,
    assertBoolean,
    assertObject,
    assertString,
    assertValuesInArray,
    assertInEnum,
} from '../../validators';
import { BaseGroup } from '../BaseGroup';
import type { BaseSettings } from '../TimeAwareBase';
import { TimeAwareBase } from '../TimeAwareBase';
import { Template } from '../template/Template';
import {
    addAssetToContainer,
    buildChildrenForSceneUsingData,
} from '../../libs/buildChildrenForScene';
import { capitalizeFirstLetter } from '../../common';
import {
    BooleanAttribute,
    getAllTemplateAttributes,
    StringAttribute,
    templateColorByIndex,
    templateFontsByIndex,
    templateWatermarkSourcesByIndex,
} from '../attributes';
import type {
    DestroyOptions,
    LifecycleAware,
    Resolution,
    SceneCategory,
    SceneTemplateReferenceMap,
} from '../../types';
import {
    AspectRatio,
    getAspectRatioFromTransitionUrl,
    ALL_ASPECT_RATIOS,
} from '../../aspectRatioConfig';
import { comparer, observable, reaction, runInAction } from '../reactivity';
import { BufferingAbortedError } from '../../errors';
import { cloneDeep } from '../../libs/cloneDeep';
import { SceneWatermarkManager } from '../watermark/SceneWatermarkManager';
import { SequentialTimeline } from '../../libs/SequentialTimeline';
import type { TickerTimeline } from '../../libs/TickerTimeline';
import { WatermarkBoundsDisplay } from '../helper/WatermarkBoundsDisplay';
import { MorphManager } from '../morph/MorphManager';
import { TransitionManager } from '../transition/TransitionManager';
import type { CanvasReusableTexturesManager } from '../../canvasReusableTexuresManager';
import type { SceneGroup } from '../SceneGroup';
import { getPartialSceneDesignConfig } from '../../video-data/designs/scenes';
import type { FancyCaptionAsset } from '../asset/FancyCaptionAsset/asset/FancyCaptionAsset';
import { syncTranscriptData } from '../asset/FancyCaptionAsset/asset/FancyCaptionAsset';
import type { Transcript } from '../Transcript';
import { ErrorReporter } from '../../libs/error-reporter';
import { VideoData } from '../../video-data';
import type { TextAsset } from '../asset/TextAsset';
import type { PlaybackRegion } from '../PlaybackRegions';
import { PlaybackRegions } from '../PlaybackRegions';
import { uniqueName } from '../asset/utils';
import { getReferenceResolution } from '../../libs/videoData';
import { DEFAULT_SCENE_IMAGE_RENDER_MODE } from '../../libs/RenderQueue';
import type { IconAsset } from '../..';
import { SubsceneManager } from './SubsceneManager';
import { SceneRenderScene } from './SceneRenderScene';
import { Subscene } from './Subscene';

export type SceneSettings = BaseSettings & {
    sceneGroup?: SceneGroup;
    timeline: TickerTimeline;
    autoDraw?: boolean;
    enableHelpers?: boolean;
    getAspectRatio: () => AspectRatio;
    reusableTextures: CanvasReusableTexturesManager;
    getReferenceResolution: () => Resolution;
};

type SceneAnimateOptions = {
    transition: boolean;
    children: boolean;
};

interface NestedPreset {
    [key: string]: NestedPreset;
}

/**
 * Maximum fraction of a scene's duration that may be consumed by the transition
 * into or out of it. Used to scale the designer's transition values when the
 * user's scene duration differs from what the designer authored.
 */
const MAX_TRANSITION_FRACTION_OF_SCENE_DURATION = 0.3;

const assetGroupPresetPropertyList = Object.freeze([
    'name',
    'classType',
    'transformation',
    'alignChildren',
    'childMaxHeightProportions',
    'spaceBetweenChildren',
    'maxHeight',
    'objectAnimation',
    'colorGraphics',
    'alphaGraphics',
]);

const textAssetPresetPropertyList = Object.freeze([
    'name',
    'classType',
    'transformation',
    'maxWidth',
    'textAlign',
    'normalStyle.animationConfigurations',
    'normalStyle.graphics',
    'highlightStyle.animationConfigurations',
    'highlightStyle.graphics',
]);

const fancyCaptionAssetPresetPropertyList = Object.freeze([
    'name',
    'classType',
    'transformation',
    'maxWidth',
    'maxHeight',
    'textAlign',
    'normalStyle.animationConfigurations',
    'normalStyle.graphics',
]);

const stylePropertiesToIgnoreList = Object.freeze([
    'objectAnimation',
    'colorGraphics',
    'alphaGraphics',
    'normalStyle',
    'spokenWordStyle',
    'highlightStyle',
    'style',
    'isSpokenWordStyleEnabled',
]);

/**
 * Scene class. This class represents a scene within a story(board). A scene is a composition of a
 * number of different objects.
 *
 * Since it inherits from a PIXI.Container is can contain children such as backgrounds, watermarks, masks, text, bars and
 * other. This class provides us with an easy to user interface to manage these children which represents a scene composition
 * for use to render.
 *
 * A video is essentially a series of scenes.
 *
 * Another important part of a slide is its duration. To represent time each slide has a Timeline object. this._timeline
 * represents the time local to the slide. All animation happens through timeline by using tweens.
 *
 */

export class Scene extends TimeAwareBase implements SfxOwner {
    override _name = 'scene';
    override _classType = 'Scene';

    #sceneGroup: SceneGroup | null = null;

    /** @internal */
    #getReferenceResolution: () => Resolution;

    /** @internal */
    _autoDraw = false;

    #textPositionPresets: Record<string, Record<string, unknown>[]> = {};
    #transitionEnabled: boolean = true;
    #getAspectRatio: () => AspectRatio;
    #template: Template | null = null;
    #watermark: SceneWatermarkManager | null = null;

    /** @internal */
    _helperScene = new PIXI.Container();
    #displayCaptionsOverride: null | boolean = null;

    /**
     * Sound effects attached to this scene. Each SFX is triggered by a specific event
     * (scene start, transition midpoint).
     */
    #sfx: SceneSfxItem[] = [];

    /**
     * Audio assets for SFX playback. Each SFX item has a corresponding AudioAsset
     * that handles the actual audio playback.
     */
    #sfxAudioAssets: Map<string, AudioAsset> = new Map();

    /**
     * The slide timeline
     * @deprecated use {@link timeline} instead
     * @internal
     */
    _timeline = new SequentialTimeline({
        name: 'scene_timeline',
        duration: Config.DEFAULT_SCENE_DURATION,
    });

    readonly #globalTimeline: TickerTimeline;

    /**
     * The slide timeline
     */
    get timeline() {
        return this._timeline;
    }

    #sceneWorkspace: BaseGroup | null = null;

    /**
     * The slide's scene contains all renderable components organized in work and debug scene. The work scene has all of
     * the components for final output, while the debug scene has all of the components that help us debug our work
     * components. We must keep these components separate as we do not want them to influence each other's bounding box.
     */
    #scene: BaseGroup | null = null;

    /**
     * Contains all stickers on the scene. It must be in its own base group in order to render on top of subscene assets
     */
    #stickers: BaseGroup | null = null;

    #morphManager: MorphManager | null = null;
    #morphEnabled = false;

    /**
     * Enables the creation of display helpers
     */
    readonly #enableHelpers: boolean;

    #renderScene: SceneRenderScene | null = null;

    #abortBuffering: (error: BufferingAbortedError) => void = () => {};

    #subsceneManager: SubsceneManager | null = null;

    #transitionManager: TransitionManager | null = null;

    @observable accessor #textDirection: (typeof Config.TEXT_DIRECTIONS)[number] =
        Config.LEFT_TO_RIGHT_TEXT_DIRECTION;
    @observable accessor #sceneDesign: string = '';
    @observable accessor #sceneCategory: SceneCategory | null = null;
    @observable accessor #aspectRatio: AspectRatio = AspectRatio.LANDSCAPE;
    @observable accessor #attribution: string | null = null;
    @observable accessor #displayWatermarkGuides = false;

    constructor(settings: SceneSettings) {
        super(settings);

        this.#getAspectRatio = settings.getAspectRatio;
        this.#getReferenceResolution = settings.getReferenceResolution;
        if (settings.hasOwnProperty('sceneGroup')) {
            this.#sceneGroup = settings.sceneGroup!;
        }
        this.#globalTimeline = settings.timeline;
        if (settings.hasOwnProperty('autoDraw')) {
            this._autoDraw = settings.autoDraw!;
        }
        this.#enableHelpers = settings.enableHelpers ?? false;

        // Create the following PIXI hierarchy.
        // + sceneWorkSpace
        // +-+ renderScene (sceneRenderScene)
        //   +-+ scene (contains scene level assets)
        //   +-+ subsceneManager (contains subscenes)
        //   +-+ stickers (contains scene level sticker assets)
        //   +-+ helperScene
        // +-+ transitionManager

        this.#sceneWorkspace = new BaseGroup(this, {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            timeline: this.#globalTimeline,
            isFinalTopLevel: true,
            name: 'sceneWorkspace',
            assetNetworkManager: this.root._assetNetworkManager,
            getAspectRatio: () => this.#getAspectRatio(),
            reusableTextures: settings.reusableTextures,
            getReferenceResolution: () => this.#getReferenceResolution(),
            getOutputResolution: () => this.root.outputResolution,
        });
        this.#sceneWorkspace.visible = false;

        /**
         * Within the Scene, there are two trees of objects:
         *  1. this.#scene: the objects parented directly onto the scene
         *  2. this._subsceneManager: the subtree of objects parented onto a subscene
         *
         * We then use the #renderScene to join these two trees together at the very
         * top of the object hierarchy.
         */
        this.#renderScene = new SceneRenderScene(this, {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            timeline: this.#globalTimeline,
            isFinalTopLevel: true,
            enableBoundsWithoutAnimationForSubtree: true,
            name: 'sceneRenderScene',
            // each time our children our built (and drawn) we emit 'childUpdated' here. This in turn triggers local
            // updates (see on childUpdated) and finally bubbles up as a global stateChanged.
            updateEventSource: this,
            assetNetworkManager: this.root._assetNetworkManager,
            getAspectRatio: () => this.#getAspectRatio(),
            reusableTextures: settings.reusableTextures,
            getReferenceResolution: () => this.#getReferenceResolution(),
            getOutputResolution: () => this.root.outputResolution,
        });

        this.#scene = new BaseGroup(this, {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            timeline: this.#globalTimeline,
            isFinalTopLevel: false,
            name: 'sceneScene',
            updateParentOnRedraw: true,
            assetNetworkManager: this.root._assetNetworkManager,
            getAspectRatio: () => this.#getAspectRatio(),
            reusableTextures: settings.reusableTextures,
            getReferenceResolution: () => this.#getReferenceResolution(),
            getOutputResolution: () => this.root.outputResolution,
        });

        this.#subsceneManager = new SubsceneManager(this, {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            timeline: this.#globalTimeline,
            isFinalTopLevel: false,
            name: 'subsceneManager_1',
            updateParentOnRedraw: true,
            assetNetworkManager: this.root._assetNetworkManager,
            getAspectRatio: () => this.#getAspectRatio(),
            reusableTextures: settings.reusableTextures,
            getReferenceResolution: () => this.#getReferenceResolution(),
            getOutputResolution: () => this.root.outputResolution,
        });

        this.#stickers = new BaseGroup(this, {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            timeline: this.#globalTimeline,
            isFinalTopLevel: false,
            name: 'stickers',
            updateParentOnRedraw: true,
            assetNetworkManager: this.root._assetNetworkManager,
            getAspectRatio: () => this.#getAspectRatio(),
            reusableTextures: settings.reusableTextures,
            getReferenceResolution: () => this.#getReferenceResolution(),
            getOutputResolution: () => this.root.outputResolution,
        });

        this.#morphManager = new MorphManager(this);

        // don't propagate transitionManager changes, instead, customize the work on each update
        this.#transitionManager = new TransitionManager(this, {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            timeline: this.#globalTimeline,
            isFinalTopLevel: true,
            name: 'transitionManager',
            updateParentOnRedraw: false,
            assetNetworkManager: this.root._assetNetworkManager,
            getAspectRatio: () => this.#getAspectRatio(),
            reusableTextures: settings.reusableTextures,
            getReferenceResolution: () => this.#getReferenceResolution(),
            getOutputResolution: () => this.root.outputResolution,
        });
        this.#transitionManager.on('childUpdated', () => {
            // once we updated this transition manager, we need to re-evaluate root so we re-process all transition
            // managers
            this.root.atomicUpdate(() => {
                this.root._updateTransition();
            });

            // avoid extra work by verifying if the transition manager has an effect on our slide
            if (!this._transitionManager || this._transitionManager.hasNoneTransition) {
                this.sceneGroup?.emit('childUpdated');
            } else {
                this.sceneGroup?.emit('childUpdated', this.name, this.localIndex, -1);
            }
        });

        this._renderScene.insertAsset(this.#scene);
        this._renderScene.insertAsset(this._subsceneManager);
        this._renderScene.insertAsset(this.#stickers);
        this._renderScene._addPixiChild(this._helperScene);
        this.#sceneWorkspace.insertAsset(this._renderScene);
        this.#sceneWorkspace.insertAsset(this.#transitionManager);

        // Add container timelines to scene timeline.
        this._timeline.add(this._subsceneManager._timeline);

        // create template
        this.#template = new Template({
            autoUpdate: this.autoUpdate,
            parent: this,
            fetchResources: this.fetchResources,
        });

        // events
        this.#setupOnStart();
        this.#setupOnEnd();

        // We capture any child changes here, so we can manually handle any other updates to reduce the number of update
        // loops
        this.on('childUpdated', (_name, subsceneIndex) => {
            if (this.#enableHelpers) {
                Scene.#updateHelpers(
                    this._helperScene,
                    this.#getAssetContainers({ deep: true }),
                    this.displayWatermarkGuides
                        ? {
                              referenceResolution: this.#getReferenceResolution(),
                              aspectRatio: this._aspectRatio,
                              watermark: this.template.watermark,
                          }
                        : null,
                );
            }
            // The watermark could be hidden or bumped if text assets have changed size or position
            // within the scene, which is why we require an update.
            this.watermark._checkAndFixProperties();
            this.watermark._update();

            // our scene duration is based on the duration of our subscenes so we need to update it
            this.batchRun(() =>
                this.getAssetsByType(Config.FANCY_CAPTION_ASSET_TYPE).forEach((asset) =>
                    syncTranscriptData(asset, this.#requiredSceneGroup.originalTranscript),
                ),
            );

            Scene.#updateAssetEndAnimation(this.scene.getAssets({ deep: false }));

            if (this.morphEnabled || this.prevScene?.morphEnabled) {
                this.root.atomicUpdate(() => {
                    MorphManager.propagateLinkedProperties(this);
                    MorphManager.updateAdjacentMorphAnimations(this);
                });
            }

            // a slide is managed by the scene group so pass on the childUpdated event so it can determine how to handle
            // this change.
            this.sceneGroup?.emit('childUpdated', this.name, this.localIndex, subsceneIndex ?? -1);
        });

        this.#watermark = new SceneWatermarkManager({
            parent: this,
            fetchResources: this.fetchResources,
            getAspectRatio: () => this.#getAspectRatio(),
        });

        // `existing` is compared by reference under `comparer.shallow`, which is the signal we want: `clear()`
        // rebuilds the asset from the design, so the name comes back identical while the instance does not.
        this.addReaction(
            reaction(
                () => ({
                    attribution: this.attribution,
                    aspectRatio: this._aspectRatio,
                    existing: this.scene.getAssetByName('attribution_1', { deep: false }) as
                        | TextAsset
                        | undefined,
                }),
                ({ attribution, aspectRatio, existing }) =>
                    this.batchRun(() => {
                        if (!attribution) {
                            existing?.destroy();
                            return;
                        }

                        const asset = existing ?? this.#createAttributionAsset(aspectRatio);
                        asset.text = capitalizeFirstLetter(attribution);
                    }),
                { equals: comparer.shallow, name: 'Scene.attribution' },
            ),
        );

        this.addReaction(
            reaction(
                () => ({
                    textDirection: this.textDirection,
                    targets: this.getAssets().filter(
                        (asset) => Config.isTextAsset(asset) || Config.isFancyCaptionAsset(asset),
                    ),
                }),
                ({ textDirection, targets }) =>
                    this.batchRun(() => Scene.#updateTextDirection(targets, textDirection)),
                {
                    // Structural, not shallow: shallow would compare `targets` by reference, and the
                    // expression allocates a new array every evaluation, so nothing would ever match.
                    equals: comparer.structural,
                    name: 'Scene.textDirection',
                },
            ),
        );

        this.addReaction(
            reaction(
                () => this.scene.getAssets({ deep: false }),
                (sceneAssets) => this.batchRun(() => Scene.#updateAssetEndAnimation(sceneAssets)),
                { equals: comparer.shallow, name: 'Scene.assetEndAnimation' },
            ),
        );

        this.addReaction(
            reaction(
                () => this.getAssetsByType(Config.FANCY_CAPTION_ASSET_TYPE),
                (captionAssets) =>
                    this.batchRun(() =>
                        captionAssets.forEach((asset) =>
                            syncTranscriptData(asset, this.#requiredSceneGroup.originalTranscript),
                        ),
                    ),
                { equals: comparer.shallow, name: 'Scene.fancyCaptionDurations' },
            ),
        );

        // the bump on overlapping text bounds is not observable, so that half stays on `childUpdated`
        // not immediate: the data-only read path has no update loop, so building the watermark asset here would
        // report a media asset for video data that contains none
        this.addReaction(
            reaction(
                () => this._aspectRatio,
                () => this.batchRun(() => this.watermark._update()),
                { name: 'Scene.watermark', fireImmediately: false },
            ),
        );

        // helper positions come from rendered bounds, so this only covers the properties they read
        this.addReaction(
            reaction(
                () => ({
                    displayWatermarkGuides: this.displayWatermarkGuides,
                    aspectRatio: this._aspectRatio,
                }),
                ({ displayWatermarkGuides, aspectRatio }) => {
                    if (!this.#enableHelpers) {
                        return;
                    }
                    this.batchRun(() =>
                        Scene.#updateHelpers(
                            this._helperScene,
                            this.#getAssetContainers({ deep: true }),
                            displayWatermarkGuides
                                ? {
                                      referenceResolution: this.#getReferenceResolution(),
                                      aspectRatio,
                                      watermark: this.template.watermark,
                                  }
                                : null,
                        ),
                    );
                },
                { equals: comparer.structural, name: 'Scene.helpers' },
            ),
        );
    }

    get transitionEnabled() {
        return this.#transitionEnabled;
    }

    set transitionEnabled(transitionEnabled: boolean) {
        if (this.#transitionEnabled === transitionEnabled) {
            return;
        }
        assertBoolean(transitionEnabled);
        this.#transitionEnabled = transitionEnabled;
        this.requiresUpdate = true;
    }

    get displayCaptions() {
        if (this.#displayCaptionsOverride !== null) {
            return this.#displayCaptionsOverride;
        }
        const captionsManager = this.sceneGroup?.captions;
        if (!captionsManager) return true;

        const globalCaptionAsset = captionsManager.asset;
        if (!globalCaptionAsset) return true;

        if (globalCaptionAsset.classType !== Config.FANCY_CAPTION_ASSET_TYPE) {
            return true;
        }

        const hasFancyCaption = this.getAssetsByType(Config.FANCY_CAPTION_ASSET_TYPE).length > 0;
        return !hasFancyCaption;
    }

    set displayCaptions(value: boolean) {
        if (this.#displayCaptionsOverride === value) {
            return;
        }
        assertBoolean(value);
        this.#displayCaptionsOverride = value;
        this.#emitChildUpdate();
    }

    /** @internal */
    _displayCaptionsOverrideCustomLoad(value: boolean | null) {
        assertBoolean(value as boolean, true);
        this.#displayCaptionsOverride = value;
    }

    /** @internal */
    _displayCaptionsOverrideCustomSave() {
        return this.#displayCaptionsOverride;
    }

    get sceneWorkspace(): BaseGroup {
        if (this.#sceneWorkspace === null) {
            throw new Error('Scene sceneWorkspace accessed before construction completed');
        }
        return this.#sceneWorkspace;
    }

    get sceneGroup(): SceneGroup | null {
        return this.#sceneGroup;
    }

    /** The scene group, for internal code paths that require the scene to belong to one. */
    get #requiredSceneGroup(): SceneGroup {
        if (this.#sceneGroup === null) {
            throw new Error('Scene is not part of a scene group');
        }
        return this.#sceneGroup;
    }

    /** The transition manager, for internal code paths that run before teardown. */
    get #requiredTransitionManager(): TransitionManager {
        if (this.#transitionManager === null) {
            throw new Error('Scene transitionManager accessed after teardown');
        }
        return this.#transitionManager;
    }

    /** @internal */
    get _renderScene(): SceneRenderScene {
        if (this.#renderScene === null) {
            throw new Error('Scene _renderScene accessed before construction completed');
        }
        return this.#renderScene;
    }

    /** @internal */
    get _subsceneManager(): SubsceneManager {
        if (this.#subsceneManager === null) {
            throw new Error('Scene _subsceneManager accessed before construction completed');
        }
        return this.#subsceneManager;
    }

    /** @internal */
    get _transitionManager(): TransitionManager | null {
        return this.#transitionManager;
    }

    get customTransition() {
        return this.#transitionManager;
    }

    get prevScene(): Scene | undefined {
        return this.#sceneGroup?._prevScene(this.id) ?? undefined;
    }

    get nextScene(): Scene | undefined {
        return this.#sceneGroup?._nextScene(this.id) ?? undefined;
    }

    /**
     * The templateReferenceMap contains a map of all child properties which reference a template property in our
     * current configuration. We use this map to copy any template properties, such as colours or fonts, to their
     * corresponding component properties.  For instance, template.color[0] can be mapped to the ColorAsset.color property
     * by assigning it the `themeRef_colors_0` reference.
     */
    buildTemplateReferenceMap(): SceneTemplateReferenceMap {
        const referenceMap: SceneTemplateReferenceMap = {};
        this.getAssets().forEach((child) => {
            referenceMap[child.name] = child.buildTemplateReferenceMap();
        });
        return referenceMap;
    }

    override get slide() {
        return this;
    }

    override get container(): BaseAsset {
        return this as unknown as BaseAsset;
    }

    get scene(): BaseGroup {
        if (this.#scene === null) {
            throw new Error('Scene scene accessed before construction completed');
        }
        return this.#scene;
    }

    get stickers(): BaseGroup {
        if (this.#stickers === null) {
            throw new Error('Scene stickers accessed before construction completed');
        }
        return this.#stickers;
    }

    get morphEnabled(): boolean {
        return this.#morphEnabled;
    }

    set morphEnabled(value: boolean) {
        assertBoolean(value);
        if (this.#morphEnabled === value) {
            return;
        }
        this.#morphEnabled = value;
        this.requiresUpdate = true;
    }

    @BooleanAttribute()
    accessor isPrePackagedMorphWithNextScene = false;

    // Which match-cut technique was applied to this scene's transition (persisted so reload replays it
    // instead of re-scoring). See Config.ALL_MATCH_CUT_TECHNIQUES.
    @StringAttribute({ choices: Config.ALL_MATCH_CUT_TECHNIQUES })
    accessor matchCutTechnique: (typeof Config.ALL_MATCH_CUT_TECHNIQUES)[number] =
        Config.MATCH_CUT_NONE;

    /** @internal */
    _setupMorphAnimations() {
        this.#morphManager!.setupMorphAnimations();
    }

    override get template() {
        return this._template;
    }

    /**
     * The current API version
     */
    get version(): Version {
        return VERSION;
    }

    /**
     * List subscenes
     * @deprecated use {@link subscenes} instead
     */
    get subslides(): Subscene[] {
        return this.subscenes;
    }

    /**
     * List subscenes
     */
    get subscenes(): Subscene[] {
        // forwards the SubsceneManager's subscene interface
        if (this.destroyed) {
            return [];
        }
        return this._subsceneManager.subscenes;
    }

    /**
     * Add a new subscene. By default, adds it to the end.
     * @param index - optionally specify the index where to add the subscene
     */
    addSubscene(index?: number): Subscene {
        return this._subsceneManager.addSubscene(true, index, undefined, false);
    }

    removeSubscene(index: number) {
        this._subsceneManager.removeSubscene(index);
    }

    /** @deprecated Use removeSubscene instead */
    removeSubslide(index: number) {
        this.removeSubscene(index);
    }

    /**
     * Recursively get all of the child assets
     */
    getAssets(options: GetAssetsOptions = { deep: true }): Asset[] {
        return this.#gatherAssets(this.#getAssetContainers(options), options);
    }

    #gatherAssets(containers: BaseGroup[], options: GetAssetsOptions): Asset[] {
        return containers
            .map((child) => child.getAssets(options))
            .flat()
            .filter(Config.isAsset);
    }

    /** Scene-level content plus the last subscene only; earlier subscenes have already played. */
    getAssetsAtSceneEnd(): Asset[] {
        const lastSubscene = this._subsceneManager.subscenes.at(-1);
        return this.#gatherAssets(
            [this.scene, this.stickers, ...(lastSubscene ? [lastSubscene] : [])],
            { deep: true },
        );
    }

    /** Scene-level content plus the first subscene only; later subscenes have not played yet. */
    getAssetsAtSceneStart(): Asset[] {
        const firstSubscene = this._subsceneManager.subscenes.at(0);
        return this.#gatherAssets(
            [this.scene, this.stickers, ...(firstSubscene ? [firstSubscene] : [])],
            { deep: true },
        );
    }

    /**
     * Returns true if the scene has any audio (media audio, scene SFX, or asset SFX).
     */
    get hasAudio(): boolean {
        if (this.#sfx.length > 0) {
            return true;
        }
        return this.getAssets().some((asset) => asset.hasAudio);
    }

    /**
     * Get the containers which directly contain and manage assets.
     */
    #getAssetContainers(options: GetAssetsOptions = { deep: false }): BaseGroup[] {
        const containers = [this.scene, this.stickers];
        if (options.deep) {
            containers.push(...this._subsceneManager._subscenes);
        }
        return containers;
    }

    /**
     * Get all lifecycle-aware objects that participate in lifecycle events (canPlay, isLoaded, etc.)
     * and update propagation (autoUpdate, autoDraw).
     */
    protected override _getLifecycleAwareObjects(): LifecycleAware[] {
        if (this.destroyed) {
            return [];
        }
        const objects: (LifecycleAware | null)[] = [
            ...this.#getAssetContainers({ deep: true }),
            this.#transitionManager,
            ...this.#sfxAudioAssets.values(),
            this._template,
            this.watermark,
        ];
        return objects.filter((obj): obj is LifecycleAware => obj !== null && !obj.destroyed);
    }

    /**
     * Get the classTypes of all of our descendants
     */
    getAssetTypes(options?: GetAssetsOptions): string[] {
        return uniq(
            this.#getAssetContainers(options)
                .map((child) => child.getAssetTypes(options))
                .flat(),
        );
    }

    /**
     * Get all assets of the given classType in our hierarchy.
     * @param classType - the classType of the component we're searching for; typically
     *                             Config.TEXT_ASSET_TYPE or Config.MEDIA_ASSET_TYPE
     */
    getAssetsByType<T extends AssetType>(
        classType: T,
        options?: GetAssetsOptions,
    ): GetAssetType<T>[];
    getAssetsByType(
        classType = Config.MEDIA_ASSET_TYPE,
        options: GetAssetsOptions = { deep: true },
    ) {
        return this.#getAssetContainers(options)
            .map((child) => child.getAssetsByType(classType, options))
            .flat();
    }

    hasAssetByName(name: string, options: GetAssetsOptions = { deep: true }): boolean {
        return this.#getAssetContainers(options).some((child) =>
            child.hasAssetByName(name, options),
        );
    }

    getAssetByName<T extends AssetName>(
        name: T,
        options?: GetAssetsOptions,
    ): GetAssetTypeByName<T> | undefined;
    getAssetByName(name: string, options: GetAssetsOptions = { deep: true }): Asset | undefined {
        return this.getAssetsByName(name, options)[0];
    }

    getAssetsByName(name: string, options: GetAssetsOptions = { deep: true }): Asset[] {
        return this.#getAssetContainers(options)
            .map((child) => child.getAssetByName(name, options))
            .filter((child) => child !== undefined);
    }

    getAssetById(id: string, options: GetAssetsOptions = { deep: true }): Asset | undefined {
        for (const container of this.#getAssetContainers(options)) {
            const child = container.getAssetById(id, options);

            if (child) {
                return child;
            }
        }
    }

    /**
     * Type-safe version of getAssetById
     * If you know the type of the asset you're looking for, use this method to avoid type errors.
     * If the asset is not found, an error will be thrown.
     */
    getAsset<T extends AssetType>(
        id: string,
        classType: T,
        options: GetAssetsOptions = { deep: true },
    ): GetAssetType<T> {
        const asset = this.getAssetById(id, { deep: options.deep });
        if (!asset) {
            ErrorReporter.setContext('slide', { id, classType, options });
            throw new Error('Asset with id not found');
        }

        if (classType !== asset.classType) {
            ErrorReporter.setContext('slide', { id, classType, options });
            throw new Error('Asset is not the expected classType');
        }
        return asset as GetAssetType<T>;
    }

    /**
     * Generates new ids for all assets on the scene. This is important when creating a new
     * scene from a static config, in order to prevent re-using the config ids in the video data.
     */
    generateNewAssetIds() {
        this.scene.getAssets().forEach((child) => {
            child.generateNewId();
        });
        this.stickers.getAssets().forEach((child) => {
            child.generateNewId();
        });
        this._subsceneManager.generateNewAssetIds();
    }

    override destroy(options?: DestroyOptions) {
        this.clearMorphAnimations();

        super.destroy(options);
        this.#abortBuffering(
            new BufferingAbortedError(this.classType, this.index, 'destroy called'),
        );

        if (this.#transitionManager) {
            this.#transitionManager.destroy(options);
            this.#transitionManager = null;
        }

        if (this._timeline) {
            this._timeline.destroy();
        }

        Scene.#removeHelpers(this._helperScene);
        this.#clearSfxAudio();

        this._renderScene.destroy(options);
        this.sceneWorkspace.destroy(options);
    }

    #setupOnStart() {
        this._timeline.on('start', () => this._propagatePlay());
    }

    #setupOnEnd() {
        this._timeline.on('end', () => this._propagatePause());
    }

    /** @internal */
    _propagatePlay() {
        // called when playback is started
        this.getAssets().forEach((child) => {
            child._propagatePlay();
        });
    }

    /** @internal */
    _emitAssetsCanPlay() {
        if (this.sceneGroup) {
            this.sceneGroup.emit('assetsCanPlay');
        }
    }

    updateForRender() {
        this.#requiredTransitionManager.updateForRender();
        const assets = this.getAssets();
        assets.forEach((asset) => asset.updateForRender());
        // Collect the names of every asset referenced as a track matte by another.
        const matteSourceNames = new Set<string>();
        assets.forEach((asset) => {
            asset.trackMattes.forEach((matte) => matteSourceNames.add(matte.assetName));
        });
        // Second pass: shapes are now up to date, so hide matte sources and build the mattes.
        assets.forEach((asset) => {
            asset._setUsedAsMatteSource(matteSourceNames.has(asset.name));
            asset._updateMatteForRender();
        });
    }

    /**
     * Remove all animation
     */
    clearAnimation() {
        this._subsceneManager.clearAnimation();
    }

    get number() {
        return this.index + 1;
    }

    /**
     * Returns the global index of the current scene. In other words, the position of the scene into relation to
     * all other scenes in the currently Luminary instance.
     */
    get globalIndex() {
        return this.index;
    }

    /**
     * Returns the local index of the current scene into relation to its parent, the sceneGroup
     */
    get localIndex() {
        return this.#requiredSceneGroup._scenes.indexOf(this);
    }

    /**
     * @deprecated use {@link globalIndex} if absolutely necessary. Preferably, refactor by using sceneGroup or local scene index.
     */
    get index() {
        // TODO: refactor localIndex to become index and keep the behaviour of global index
        return this.root.scenes.indexOf(this);
    }

    /**
     * Returns the position of the playback head of the slide
     */
    get currentTime() {
        return this._timeline.currentTime;
    }

    /**
     * Returns the scene's start time relative to luminary. This is the time when a scene is first
     * included in the render, but may not be visible due to a transition
     */
    get startTime(): number {
        return this._timeline.startTimeRelativeToAncestor(this.#globalTimeline);
    }

    /**
     * Get the scene's end time relative to luminary. This is the time when a scene is no longer
     * included in the render, but it may be no longer visible before this time due to a transition.
     */
    get endTime(): number {
        return this.startTime + this.duration;
    }

    /**
     * The slide's duration in milliseconds
     */
    get duration(): number {
        return this._subsceneManager._timeline.duration;
    }

    set duration(value: number) {
        if (value === this.duration) {
            return;
        }
        assertNumberBetween(value, Config.MIN_SCENE_DURATION, Infinity);
        this.batchRun(() => {
            if (!this._subsceneManager._timeline.hasChildren) {
                this._subsceneManager._timeline.duration = value;
                this.getAssets().forEach((asset) => {
                    asset.requiresRedraw = true;
                });
                return;
            }

            this._subsceneManager._durationToApply = value;
            this._subsceneManager._updateSubsceneDurationBasedOnTextLength(value);
            this.getAssets().forEach((asset) => {
                asset.requiresRedraw = true;
            });
        });
        this.requiresUpdate = true;
    }

    get startTimeRelativeToSceneGroup(): number {
        return this.startTime - this.#requiredSceneGroup.startTime;
    }

    get endTimeRelativeToSceneGroup(): number {
        return this.endTime - this.#requiredSceneGroup.startTime;
    }

    /**
     * The duration the incoming transition overlaps this scene by, in whole milliseconds.
     *
     * `duration` / `prevDuration` default to this scene's and the previous scene's current
     * durations. Pass them to evaluate the transition for hypothetical durations without mutating
     * anything — used by {@link calculateDurationForLogicalSpan} to invert this function.
     */
    calculateTransitionDuration(
        duration: number = this.duration,
        prevDuration: number = this.prevScene?.duration || 0,
    ): number {
        if (this.destroyed) {
            return 0;
        }
        if (!this.root.template.transitionsEnabled) {
            return 0;
        }
        if (!this.transitionEnabled || !this.prevScene) {
            return 0;
        }

        const overlapDurationBeforeScaling =
            this.#requiredTransitionManager.extractedData.overlapDuration;
        const scaleFactor = this._calculateTransitionScaleFactor(duration, prevDuration);
        const overlapDuration = Math.round(overlapDurationBeforeScaling * scaleFactor);

        return overlapDuration;
    }

    /**
     * The scene's duration excluding the transition into it.
     */
    getLogicalDuration(): number {
        return this.duration - this.calculateTransitionDuration();
    }

    /**
     * The scene's start time relative to luminary, excluding accumulated transitions
     * within the scene group.
     */
    getLogicalStartTime(): number {
        let startTime = this.#requiredSceneGroup.startTime;
        for (const scene of this.#requiredSceneGroup._scenes) {
            if (scene === this) return startTime;
            startTime += scene.getLogicalDuration();
        }
        return startTime;
    }

    /**
     * The scene's end time relative to luminary, excluding accumulated transitions
     * within the scene group.
     */
    getLogicalEndTime(): number {
        return this.getLogicalStartTime() + this.getLogicalDuration();
    }

    /**
     * Inverse of calculateTransitionDuration: given a desired logical span (the
     * scene's visible duration after the incoming transition has carved out its
     * overlap), return the scene.duration that produces it.
     *
     * Pass `targetPrevSceneDuration` when the caller is changing both this scene's
     * and the previous scene's durations in the same batch; otherwise the previous
     * scene's current duration is used.
     */
    calculateDurationForLogicalSpan(
        targetLogicalSpan: number,
        { targetPrevSceneDuration }: { targetPrevSceneDuration?: number } = {},
    ): number {
        if (this.destroyed) {
            return targetLogicalSpan;
        }
        if (!this.root.template.transitionsEnabled) {
            return targetLogicalSpan;
        }
        if (!this.transitionEnabled || !this.prevScene) {
            return targetLogicalSpan;
        }

        const overlap0 = this.#requiredTransitionManager.extractedData.overlapDuration;
        const inDur = this.#requiredTransitionManager.extractedData.inTransitionDuration;
        const outDur = this.#requiredTransitionManager.extractedData.outTransitionDuration;
        if (overlap0 <= 0 || !inDur || !outDur) {
            return targetLogicalSpan;
        }

        const prevD = targetPrevSceneDuration ?? this.prevScene.duration;
        const frac = MAX_TRANSITION_FRACTION_OF_SCENE_DURATION;

        const dCapped = targetLogicalSpan + Math.round(overlap0);
        const dPrevClamped =
            targetLogicalSpan + Math.round((overlap0 * Math.trunc(prevD * frac)) / outDur);
        const durationScaledDenominator = 1 - (overlap0 * frac) / inDur;
        const durationScaled =
            durationScaledDenominator > 0
                ? targetLogicalSpan / durationScaledDenominator
                : Number.POSITIVE_INFINITY;

        const candidates = [dCapped, dPrevClamped, durationScaled].filter(
            (d) => Number.isFinite(d) && d > 0,
        );
        if (candidates.length === 0) {
            return targetLogicalSpan;
        }

        // The candidates pick the binding regime but only approximate the duration.
        // The transition duration depends on the duration of the scene itself, so changing the duration
        // of the scene changes the duration of the transition, which changes the duration of the scene, etc.
        // Because the transition is always snapped to a whole millisecond and the transition is capped
        // to 30% of the scene duration, we can find the correct duration with 2 iterations.
        let duration = Math.min(...candidates);
        duration = targetLogicalSpan + this.calculateTransitionDuration(duration, prevD);
        duration = targetLogicalSpan + this.calculateTransitionDuration(duration, prevD);
        return duration;
    }

    _calculateTransitionScaleFactor(
        duration: number = this.duration,
        prevDuration: number = this.prevScene?.duration || 0,
    ): number {
        // Transition values extracted from transition JSON are scaled such that
        // the transition fits within the transition duration set by the designer.
        // But the user's scene duration may not be the same as the designer's scene duration.
        // So, scale the values to fit the transition within 30% of the scene duration.
        // We do this by finding the scale factor for in-transition and out-transition.
        // The lesser of the 2 scale factors is used to scale all values.
        // Here, we do not have to worry whether the transition is overlapping or non-overlapping
        // because we scale in-transition duration, out-transition duration and overlap duration.

        if (this.destroyed) {
            return 1;
        }

        const inTransitionDuration =
            this.#requiredTransitionManager.extractedData.inTransitionDuration;

        const outTransitionDuration =
            this.#requiredTransitionManager.extractedData.outTransitionDuration;
        const cappedInTransitionDuration = Math.min(
            inTransitionDuration,
            Math.trunc(duration * MAX_TRANSITION_FRACTION_OF_SCENE_DURATION),
        );
        const inTransitionScaleFactor = inTransitionDuration
            ? cappedInTransitionDuration / inTransitionDuration
            : 0;

        const cappedOutTransitionDuration = Math.min(
            outTransitionDuration,
            Math.trunc(prevDuration * MAX_TRANSITION_FRACTION_OF_SCENE_DURATION),
        );
        const outTransitionScaleFactor = outTransitionDuration
            ? cappedOutTransitionDuration / outTransitionDuration
            : 0;
        const scaleFactor = Math.min(inTransitionScaleFactor, outTransitionScaleFactor);
        return scaleFactor;
    }

    /**
     * Add a brand new asset of type assetType to scene
     */
    addAsset<T extends AssetType>(
        assetType: T,
        settings?: Partial<BaseAssetSettings>,
    ): GetAssetType<T>;
    /**
     * @param data - object created by asset.save()
     */
    addAsset(
        data: Record<string, unknown>,
        settings?: Partial<BaseAssetSettings>,
    ): GetAssetType<unknown>;
    addAsset(
        assetTypeOrData: AssetType | Record<string, unknown>,
        settings: Partial<BaseAssetSettings> = {},
    ) {
        const receivedData = typeof assetTypeOrData !== 'string';
        let assetType: AssetType;
        if (receivedData) {
            assetType = assetTypeOrData.classType as AssetType;
            if (assetTypeOrData.id) {
                settings = { ...settings, id: assetTypeOrData.id as string };
            }
            if (assetTypeOrData.name) {
                settings = { ...settings, name: assetTypeOrData.name as string };
            }
        } else {
            assetType = assetTypeOrData;
        }
        const container =
            settings.createdFrom === AssetCreatedFrom.STICKER ? this.stickers : this.scene;
        const asset = addAssetToContainer(
            container as Parameters<typeof addAssetToContainer>[0],
            assetType,
            settings,
        );

        if (receivedData) {
            asset.load(assetTypeOrData as RawAssetData);
        }

        if (Config.isTextAsset(asset) || Config.isFancyCaptionAsset(asset)) {
            asset.textDirection = this.textDirection;
        }

        if (assetTypeOrData === Config.FANCY_CAPTION_ASSET_TYPE) {
            const fancyCaptionAsset = asset as FancyCaptionAsset;
            fancyCaptionAsset._batchRedraw(() =>
                syncTranscriptData(fancyCaptionAsset, this.#requiredSceneGroup.originalTranscript),
            );
        }

        return asset;
    }

    addSticker<T extends AssetType>(assetType: T): GetAssetType<T> {
        assertInEnum(assetType, Config.ALL_STICKER_ASSET_TYPES);
        const name = uniqueName(
            'sticker',
            this.getAssets()
                .filter(Config.isStickerAsset)
                .map((asset) => asset.name),
        );
        return this.addAsset(assetType, {
            name,
            createdFrom: AssetCreatedFrom.STICKER,
        });
    }

    /**
     * Add a brand new asset of type assetType to the subscene
     */
    addAssetToSubscene<T extends AssetType>(
        assetType: T,
        settings?: Partial<BaseAssetSettings>,
    ): GetAssetType<T>;
    /**
     * @param data - object created by asset.save()
     */
    addAssetToSubscene(
        data: Record<string, unknown>,
        settings?: Partial<BaseAssetSettings>,
    ): GetAssetType<unknown>;
    addAssetToSubscene(assetTypeOrData: AssetType | Record<string, unknown>, settings = {}) {
        if (this.subscenes.length > 1) {
            console.warn(
                'More than 1 subscene present but `addAssetToSubscene()` only adds it to the _first_ subscene. You might want to call this before adding more subscenes',
            );
        }
        const receivedData = typeof assetTypeOrData !== 'string';
        let assetType: AssetType;
        if (receivedData) {
            assetType = assetTypeOrData.classType as AssetType;
            if (assetTypeOrData.id) {
                settings = { ...settings, id: assetTypeOrData.id as string };
            }
            if (assetTypeOrData.name) {
                settings = { ...settings, name: assetTypeOrData.name as string };
            }
        } else {
            assetType = assetTypeOrData;
        }
        const asset = addAssetToContainer(
            this._subsceneManager.subscenes[0] as Parameters<typeof addAssetToContainer>[0],
            assetType,
            settings,
        );

        if (receivedData) {
            asset.load(assetTypeOrData as RawAssetData);
        }

        if (Config.isTextAsset(asset) || Config.isFancyCaptionAsset(asset)) {
            asset.textDirection = this.textDirection;
        }

        return asset;
    }

    /**
     * Run tasks in batch by setting autoUpdate and autoDraw to false temporarily
     */
    batchRun<T>(callback: () => T): T {
        return runInAction(() => {
            const initAutoDraw = this.autoDraw;
            const initAutoUpdate = this.autoUpdate;
            this.autoUpdate = false;
            this.autoDraw = false;

            try {
                return callback();
            } finally {
                this.autoUpdate = initAutoUpdate;
                this.autoDraw = initAutoDraw;
            }
        });
    }

    /**
     * Load the slide contents using slide data (video_data) through Luminary.load.
     */
    override load(data: OpaqueVideoData | RawSceneData) {
        assertObject(data);

        data = Migrator.updateSceneData(data as RawSceneData);

        // Temporary (LU-2946) debug transition getting mixed up
        const transitionUrl = (data as RawSceneData).customTransition?.transitionUrl;
        if (transitionUrl && !window.location?.href?.includes('dashboard')) {
            const transitionProbableAspectRatio = getAspectRatioFromTransitionUrl(transitionUrl);
            if (
                transitionProbableAspectRatio &&
                transitionProbableAspectRatio !== this.#getAspectRatio()
            ) {
                throw new Error('Transition does not match luminary aspect ratio.');
            }
        }

        this.batchRun(() => {
            this.clear();

            data = cloneDeep(data);

            super.load(data);

            buildChildrenForSceneUsingData(this.slide, data);

            this._subsceneManager.load(data as RawSceneData);
        });

        return this;
    }

    /**
     * Save Scene's state into our video-data model data schema. The result of save should
     * not be parsed or otherwise modified outside of Luminary.
     */
    override save(): OpaqueVideoData {
        // We NEED at least one subscene otherwise we can't store duration (since duration is saved at the subscene
        // level).  If there are no subscenes, add one!
        if (this.subscenes.length === 0) {
            this.addSubscene();
        }
        // we don't want to store the following components since they are defined globally
        const assetsToSkip = ['watermark_1', 'attribution_1'];
        const { slideChildren, subscenesChildren, stickerChildren } =
            this.#saveForAllSlideAndSublideChildren(assetsToSkip);
        const properties = super.save(true, true);
        const slideConfig = Object.assign(
            subscenesChildren,
            slideChildren,
            stickerChildren,
            properties,
        );

        slideConfig['sceneGroupId'] = this.#requiredSceneGroup.id;

        return slideConfig;
    }

    /**
     * Helper method to duplicate the scene data, without having to access
     * the json outside of luminary.
     * The original and new template data objects are required so that we can properly
     * update any template references in the scene data.
     */
    static duplicateSceneData(
        sourceSceneData: OpaqueVideoData,
        sourceTemplateData: OpaqueVideoData,
        newSceneDesignId: string,
        targetTemplateData: OpaqueVideoData,
    ): OpaqueVideoData {
        let newSceneData = cloneDeep(Migrator.updateSceneData(sourceSceneData));
        newSceneData.sceneDesign = newSceneDesignId;

        // Update any template references in the scene data
        const sourceTemplate = new Template({ fetchResources: false });
        const targetTemplate = new Template({ fetchResources: false });
        sourceTemplate.load(sourceTemplateData);
        targetTemplate.load(targetTemplateData);

        if (!newSceneDesignId.includes(targetTemplate.id)) {
            throw new Error(
                `New scene design id (${newSceneDesignId}) must include the target template id (${targetTemplate.id})`,
            );
        }

        /**
         * Look for and fix any template references that will no longer be valid in the target template.
         * For example, if the source template had 3 colours (so we have themeRef_colors_2), but the target
         * template only has 2 colours (meaning themeRef_colors_2 would be invalid). For these cases
         * we update to the first ref (themeRef_colors_0).
         */
        const findRefUpdateMappings = (
            result: [string, string][],
            oldArray: unknown[],
            targetMaxLength: number,
            refCreator: (index: number) => string,
            checkingWatermarkReferences = false,
        ) => {
            if (targetMaxLength === 0 && oldArray.length > 0) {
                if (!checkingWatermarkReferences) {
                    throw new Error(`Target template does not have ${refCreator(0)}`);
                }
                // It is ok for the target to not have any watermark references.
                return;
            }
            for (let i = targetMaxLength; i < oldArray.length; i++) {
                const oldRef = refCreator(i);
                const newRef = refCreator(0); // just always use the first reference
                result.push([oldRef, newRef]);
            }
        };

        const refsToUpdate: [string, string][] = [];
        findRefUpdateMappings(
            refsToUpdate,
            sourceTemplate.colors,
            targetTemplate.colors.length,
            templateColorByIndex,
        );
        findRefUpdateMappings(
            refsToUpdate,
            sourceTemplate.fonts,
            targetTemplate.fonts.length,
            templateFontsByIndex,
        );

        // Watermarks need special handling, the target may have existing watermarks or none at all.
        // There is nothing to do if we are duplicating to the same template.
        if (sourceTemplate.id !== targetTemplate.id) {
            const watermarkRefs: [string, string][] = [];
            findRefUpdateMappings(
                watermarkRefs,
                sourceTemplate.watermarkSources,
                targetTemplate.watermarkSources.length,
                templateWatermarkSourcesByIndex,
                true,
            );
            if (watermarkRefs.length > 0) {
                refsToUpdate.push(...watermarkRefs);
            } else {
                // There are no watermarks in the target. Make sure the data sources are set to 'inherit'.
                let dataString = JSON.stringify(newSceneData);
                dataString = dataString.replaceAll(
                    /themeRef_watermarkSources_\d+/g,
                    Config.INHERIT_FROM_TEMPLATE,
                );
                newSceneData = JSON.parse(dataString);
            }
        }

        if (refsToUpdate.length > 0) {
            let dataString = JSON.stringify(newSceneData);
            refsToUpdate.forEach(([oldRef, newRef]) => {
                dataString = dataString.replaceAll(oldRef, newRef);
            });
            newSceneData = JSON.parse(dataString);
        }

        return newSceneData;
    }

    /**
     * Helper method to get the scene category from a scene data object
     */
    static getSceneCategoryFromSceneData(sceneData: OpaqueVideoData): SceneCategory {
        // do not need to ensure new object since sceneCategory is an immutable property (string)
        const migratedSceneData = Migrator.updateSceneData(sceneData);
        return migratedSceneData.sceneCategory as SceneCategory;
    }

    #saveForAllSlideAndSublideChildren(assetsToSkip: string[] = []) {
        const slideChildren = {
            children: this.scene
                .getAssets()
                .filter((item) => !assetsToSkip.some((skip) => item.name === skip))
                .map((item) => item.save())
                .flat(),
        };
        const stickerChildren = {
            stickers: this.stickers
                .getAssets()
                .filter((item) => !item.isMatchCutDuplicate)
                .map((item) => item.save()),
        };
        const subscenesChildren = this._subsceneManager.save();
        return { slideChildren, subscenesChildren, stickerChildren };
    }

    /** @internal */
    _aspectRatioCustomSave() {
        return this._aspectRatio;
    }

    get isPlaying() {
        return this._timeline.playing() && this._timeline.active;
    }

    /**
     * Get the active subscenes at the provided scene time.
     * @internal
     */
    _getActiveSubscenesAt(currentTime: number): Subscene[] {
        return this._subsceneManager._timeline
            .getActiveChildrenIndicesAt(currentTime)
            .map((index) => this.subscenes[index]);
    }

    /**
     * Called while seeking, forwarding a request to buffer any data (if need be) down the hierarchy.
     *
     * @param bufferingMode - resolveWhenCanPlay or resolveWhenSingleFrame
     */
    async _propagateBuffering(bufferingMode = BufferingMode.ResolveWhenCanPlay): Promise<void> {
        try {
            await this.#propagateBuffering(bufferingMode);
        } catch (e) {
            if (e instanceof BufferingAbortedError) {
                // Don't propagate an error, but rather just resolve
                return;
            }
            throw e;
        }
    }

    async #propagateBuffering(bufferingMode: BufferingMode): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.#abortBuffering = reject;
            Promise.all([
                this.scene._bufferAtTime(this.currentTime, bufferingMode),
                this.stickers._bufferAtTime(this.currentTime, bufferingMode),
                this._subsceneManager._propagateBuffering(bufferingMode),
                this.#requiredTransitionManager._propagateBuffering(bufferingMode),
            ])
                .then(() => resolve())
                .catch(reject);
        });
    }

    get textDirection() {
        return this.#textDirection;
    }

    set textDirection(value) {
        if (this.textDirection === value) {
            return;
        }
        assertInArray(value, Config.TEXT_DIRECTIONS);
        this.#textDirection = value;
        this.requiresUpdate = true;
    }

    static #updateTextDirection(
        assets: Asset[],
        textDirection: (typeof Config.TEXT_DIRECTIONS)[number],
    ) {
        assets.forEach((asset) => {
            (asset as TextAsset).textDirection = textDirection;
        });
    }

    /**
     * applies a Text Position Preset from the available text positions (availableTextPositions)
     *
     * throws if a text position is provided which is not stored in availableTextPositions
     */
    applyTextPreset(presetName: (typeof Config.ALL_TEXT_POSITIONS)[number]): void {
        this.#applyTextPositionPreset(presetName);
    }

    /**
     * Gets the active Text Position Preset by analyzing current scene state.
     * @param opts.excludeStyle - If true, skips style data when calculating preset
     * @returns The matching text position preset name, or null if none match
     */
    getActiveTextPreset(
        opts: { excludeStyle: boolean } = { excludeStyle: true },
    ): (typeof Config.ALL_TEXT_POSITIONS)[number] | null {
        const excludeStyle = opts.excludeStyle;

        const activeTextPreset = (() => {
            const textPositionPreset = this._createTextPositionPreset();
            if (excludeStyle) {
                return textPositionPreset.map((item) => omit(item, stylePropertiesToIgnoreList));
            }

            return textPositionPreset;
        })();

        if (activeTextPreset.length < 1) {
            return null;
        }

        for (const key in this.textPositionPresets) {
            const preset = (() => {
                if (excludeStyle) {
                    return this.textPositionPresets[key].map((item) =>
                        omit(item, stylePropertiesToIgnoreList),
                    );
                }
                return this.textPositionPresets[key];
            })();

            if (!isEqual(preset, activeTextPreset)) {
                continue;
            }
            return key;
        }

        return null;
    }

    /**
     * The text position presets expressed in local, slide space. Each slide requires at least one valid entry.
     * Each entry should contain property overrides for all components which control our text position. Those components
     * are `text_group_1` and its children. When we only have one entry then the entry is empty or `[]`. That's because
     * it doesn't change any of the property values defined by our children when loading the scene design.
     */
    get textPositionPresets() {
        return this.#textPositionPresets;
    }

    set textPositionPresets(value) {
        assertObject(value);
        assertValuesInArray(Object.keys(value), Config.ALL_TEXT_POSITIONS);
        // assert that each child at least has a classType & name
        const textPositions = Object.keys(value);
        textPositions.forEach((textPosition) => {
            const preset = value[textPosition];
            for (let i = 0; i < preset.length; i++) {
                assertHasOwnProperties(preset[i], ['name', 'classType']);
            }
        });
        assertMinimumLength(Object.keys(value), 1);
        if (isEqual(value, this.textPositionPresets)) {
            return;
        }
        this.#textPositionPresets = value;
        this.requiresUpdate = true;
    }

    /**
     * Add a new text position preset for the given text position. Duplicates the currently set text position preset
     * value.
     */
    addTextPositionPreset(textPosition: string) {
        assertInArray(textPosition, Config.ALL_TEXT_POSITIONS);
        assertNotInArray(textPosition, this.availableTextPositions); // check that it doesn't already exist
        // copy the currently active preset to the new entry
        const currentTextPositionPreset = this._createTextPositionPreset();
        this.textPositionPresets[textPosition] = currentTextPositionPreset;
        // adding a new preset doesn't actually change what's drawn, but the state is updated!
        this.#emitChildUpdate();
    }

    /**
     * Remove a text position preset.
     */
    removeTextPositionPreset(textPosition: string) {
        assertInArray(textPosition, this.availableTextPositions);
        if (this.availableTextPositions.length <= 1) {
            throw new Error('cannot remove the last text position preset!');
        }

        delete this.textPositionPresets[textPosition];
    }

    /** @internal */
    _createTextPositionPreset() {
        const assetGroups = this.getAssetsByType(Config.GROUP_ASSET_TYPE);
        const textAssets = this.getAssetsByType(Config.TEXT_ASSET_TYPE);
        const fancyAssets = this.getAssetsByType(Config.FANCY_CAPTION_ASSET_TYPE);
        if (assetGroups.length === 0 && textAssets.length === 0 && fancyAssets.length === 0) {
            return [];
        }

        const fancyPreset = fancyAssets.map((fancyAsset) =>
            pick(fancyAsset.save(true, false, true, true), fancyCaptionAssetPresetPropertyList),
        );
        const assetGroupPreset = assetGroups.map((assetGroup) =>
            pick(assetGroup.save(true, false, true, true), assetGroupPresetPropertyList),
        );
        const textPreset = textAssets.map((textAsset) =>
            pick(textAsset.save(true, false, true, true), textAssetPresetPropertyList),
        );

        return [...fancyPreset, ...assetGroupPreset, ...textPreset];
    }

    /**
     * Save the current scene state as the state for the current textPosition.
     */
    saveTextPositionPreset(textPosition: (typeof Config.ALL_TEXT_POSITIONS)[number]) {
        const textPositionPreset = this._createTextPositionPreset();
        if (textPositionPreset.length < 1) {
            return;
        }

        if (isEqual(this.textPositionPresets[textPosition], textPositionPreset)) {
            return;
        }

        this.textPositionPresets[textPosition] = textPositionPreset;
        this.#emitChildUpdate();
    }

    /**
     * Finds the text position preset for given asset.
     * @param assetName - asset name (text_1, quote_1, etc.)
     * @param textPosition - text position (center, center_left, etc.)
     * @returns {any} text position preset of asset
     */
    #getAssetPresetForTextPosition(assetName: string, textPosition: string) {
        return this.textPositionPresets[textPosition].find(
            (preset) => preset.name === assetName,
        ) as NestedPreset | undefined;
    }

    /**
     * Copies dynamic styles applied to asset in current text position to all text positions.
     * @param assetNameOrUuid - asset name (text_1, quote_1, etc.) or UUID of the asset.
     */
    copyDynamicStylesToAllTextPositionPresets(assetNameOrUuid: string) {
        // TODO: LU-2285 - cleanup. After web is merged, this can become `assetId`.
        const assetName = isUuid(assetNameOrUuid)
            ? this.getAssetById(assetNameOrUuid)!.name
            : assetNameOrUuid;
        const selectedTextPosition = this.getActiveTextPreset();
        if (!selectedTextPosition) {
            return;
        }
        const selectedAssetPreset = this.#getAssetPresetForTextPosition(
            assetName,
            selectedTextPosition,
        );
        if (!selectedAssetPreset) {
            return;
        }

        this.availableTextPositions.forEach((textPosition) => {
            if (textPosition === selectedTextPosition) {
                return;
            }
            const selectedAssetPresetCopy = cloneDeep(selectedAssetPreset);
            const assetPreset = this.#getAssetPresetForTextPosition(assetName, textPosition);
            if (!assetPreset) {
                return;
            }

            if ((assetPreset.classType as unknown) === Config.TEXT_ASSET_TYPE) {
                ['normalStyle', 'highlightStyle'].forEach((style) => {
                    if (!selectedAssetPresetCopy[style]) {
                        return;
                    }
                    if (!assetPreset[style]) {
                        assetPreset[style] = {} as NestedPreset;
                    }
                    ['animationConfigurations', 'graphics'].forEach((property) => {
                        if (selectedAssetPresetCopy[style][property]) {
                            assetPreset[style][property] = selectedAssetPresetCopy[style][property];
                        }
                    });
                });

                return;
            }

            if ((assetPreset.classType as unknown) === Config.FANCY_CAPTION_ASSET_TYPE) {
                ['style'].forEach((style) => {
                    if (!selectedAssetPresetCopy[style]) {
                        return;
                    }
                    if (!assetPreset[style]) {
                        assetPreset[style] = {} as NestedPreset;
                    }
                    ['animationConfigurations', 'graphics'].forEach((property) => {
                        if (selectedAssetPresetCopy[style][property]) {
                            assetPreset[style][property] = selectedAssetPresetCopy[style][property];
                        }
                    });
                });
                return;
            }

            if ((assetPreset.classType as unknown) === Config.TEXT_ASSET_TYPE) {
                ['normalStyle', 'highlightStyle'].forEach((style) => {
                    if (!selectedAssetPresetCopy[style]) {
                        return;
                    }
                    if (!assetPreset[style]) {
                        assetPreset[style] = {} as NestedPreset;
                    }
                    ['objectAnimation', 'alphaGraphics', 'colorGraphics'].forEach((property) => {
                        if (selectedAssetPresetCopy[style][property]) {
                            assetPreset[style][property] = selectedAssetPresetCopy[style][property];
                        }
                    });
                });

                return;
            }

            ['objectAnimation', 'alphaGraphics', 'colorGraphics', 'animateOutBehaviour'].forEach(
                (property) => {
                    if (selectedAssetPresetCopy[property]) {
                        assetPreset[property] = selectedAssetPresetCopy[property];
                    }
                },
            );
        });
    }

    /**
     * Get all local available text positions
     */
    get availableTextPositions(): string[] {
        return Object.keys(this.textPositionPresets);
    }

    /**
     * Apply the given text position preset onto our current scene configuration
     */
    #applyTextPositionPreset(textPosition: string) {
        if (!this.availableTextPositions.includes(textPosition)) {
            ErrorReporter.setContext('slide', {
                textPosition,
                sceneDesign: this.sceneDesign,
                availableTextPositions: this.availableTextPositions,
            });
            throw new Error('Text position preset does not exist');
        }
        this.#loadTextPositionPreset(this.textPositionPresets[textPosition] as RawAssetData[]);
    }

    #loadTextPositionPreset(presetData: RawAssetData[]) {
        // disable any slide level updates while applying our presets
        const initAutoDraw = this.autoDraw;
        this.autoDraw = false;

        // Call `load()` for each asset with properties defined in the textPositionPreset
        const assets = this.getAssets();
        presetData.forEach((assetPreset) => {
            assets
                .filter((item) => item.name === assetPreset.name)
                .forEach((asset) => {
                    switch (assetPreset.classType) {
                        case Config.FANCY_CAPTION_ASSET_TYPE:
                            asset.load(
                                pick(
                                    assetPreset,
                                    fancyCaptionAssetPresetPropertyList,
                                ) as RawAssetData,
                                { skipTemplateAttributes: true },
                            );
                            break;
                        case Config.TEXT_ASSET_TYPE:
                            asset.load(
                                pick(assetPreset, textAssetPresetPropertyList) as RawAssetData,
                                { skipTemplateAttributes: true },
                            );
                            break;
                        case Config.GROUP_ASSET_TYPE:
                            asset.load(
                                pick(assetPreset, assetGroupPresetPropertyList) as RawAssetData,
                                { skipTemplateAttributes: true },
                            );
                            break;
                        default:
                            throw new Error(`Unknown asset type ${assetPreset.classType}`);
                    }
                });
        });
        // restore autoDraw setting
        this.autoDraw = initAutoDraw;
    }

    /**
     * The attribution text
     */
    get attribution(): string | null {
        return this.#attribution;
    }

    set attribution(value: string | null) {
        if (this.attribution === value) {
            return;
        }
        assertString(value as string, true);
        this.#attribution = value;
        this.requiresUpdate = true;
    }

    #createAttributionAsset(aspectRatio: AspectRatio): TextAsset {
        const asset = addAssetToContainer(
            this.scene as Parameters<typeof addAssetToContainer>[0],
            VideoData.TEXT_ASSET_TYPE,
            { name: 'attribution_1', updateEventSource: this, updateParentOnRedraw: false },
        );
        asset.load(getPartialSceneDesignConfig('partials:attribution', aspectRatio).children[0]);
        asset.generateNewId();
        this.scene.insertAsset(asset);
        return asset;
    }

    /**
     * Clear all children
     */
    override clear() {
        // one action: each sub-clear below is atomic on its own, but a sequence of them is not — the first
        // would wake `Scene.attribution`, which recreates the attribution asset before the rest have run
        runInAction(() => {
            // removing our all children resets the duration, collect it here so we can re-apply it afterwards
            const duration = this.duration;

            // remove existing asset arrangement
            this.scene.clear([this._subsceneManager]);
            this.stickers.clear();
            this._subsceneManager.clear();
            this.#requiredTransitionManager.clear();

            // clean up SFX
            this.#clearSfxAudio();

            // dequeue any render tasks for this slide
            if (this.root) {
                this.root._renderSceneQueue.dequeueTasksForScene(this.id);
            }

            // re-apply duration
            this._subsceneManager._timeline.duration = duration;

            this.requiresUpdate = true;
        });
    }

    get watermark(): SceneWatermarkManager {
        if (this.#watermark === null) {
            throw new Error('Scene watermark accessed before construction completed');
        }
        return this.#watermark;
    }

    set watermark(watermark) {
        // validate
        assertObject(watermark, true);
        if (isEqual(this.watermark, watermark)) {
            return;
        }
        this.#watermark = watermark;
        this.requiresUpdate = true;
    }

    static #updateAssetEndAnimation(sceneAssets: BaseAsset[]) {
        sceneAssets.forEach((asset) => {
            if (asset.animateOutBehaviour === Config.ANIMATE_OUT_BEHAVIOUR_WHEN_PRECEDING) {
                asset._animationAnchorsToRemove = Config.END_ANIMATION_CURVE_ANCHOR;
            }
        });
    }

    /**
     * Each slide maintains its own copy of the global template. This is so we can
     * propagate changes from the global template to each slide, without forcing a slide to
     * update whenever the index updates.
     *
     * @ignore
     */
    get _template(): Template {
        if (this.#template === null) {
            throw new Error('Scene template accessed before construction completed');
        }
        return this.#template;
    }

    /**
     * The loaded scene design id from the active template. A scene design is a collection of assets and property values,
     * resulting in a specific visual composition. This value cannot be directly set, but will reflect the last config
     * applied by [load]{@link Luminary.Scene#load}.
     */
    get sceneDesign(): string {
        return this.#sceneDesign;
    }

    /**
     * sceneDesign still needs to be loadable, but we don't want to expose a public api for it.
     * Using a custom load method allows us to achieve this.
     * @internal
     */
    _sceneDesignCustomLoad(value: string) {
        assertString(value);
        const templateId = Config.getTemplateIdFromSceneDesignId(value);
        // A design may load when:
        // - the video's own template owns it (the internal editors create and load
        //   new designs before any bundle contains them), or
        // - it arrived through the template bundle, which holds everything the
        //   backend allowed for this template: its own designs plus those of its
        //   enabled global templates, or
        // - legacy: it is owned by the original global template. Old videos can
        //   reference global captions that are no longer enabled for their
        //   template, and those must keep loading.
        if (
            templateId !== this._template.id &&
            templateId !== 'global_template' &&
            !this.root._hasSceneDesign(value)
        ) {
            ErrorReporter.setContext('slide', {
                templateId: this._template.id,
                sceneDesignId: value,
            });
            throw new Error('Cannot load scene design');
        }
        this.#sceneDesign = value;
        this.requiresUpdate = true;
    }

    /**
     * The aspect ratio. This property affect the slide's resolution.
     * @internal
     */
    get _aspectRatio(): AspectRatio {
        return this.#aspectRatio;
    }

    set _aspectRatio(value) {
        if (value === this._aspectRatio) {
            return;
        }
        assertInArray(value, ALL_ASPECT_RATIOS);
        this.#aspectRatio = value;
        this.requiresUpdate = true;
    }

    /** @internal */
    _updateAspectRatio(value: AspectRatio) {
        if (this._aspectRatio === value) {
            return;
        }
        // get asset changes
        const editableProperties = this.saveEditable();

        // Save colorMaps for DisableSwap icon assets before aspect ratio change
        // We want to preserve colors if the icon sourceData is the same in the new aspect ratio
        const disableSwapIconColorMaps = new Map<
            string,
            { sourceData: unknown; colorMaps: unknown }
        >();
        (
            this.getAssets({ deep: true }).filter(
                (asset) => asset.hasSuffix(AssetSuffix.DisableSwap) && Config.isIconAsset(asset),
            ) as IconAsset[]
        ).forEach((asset: IconAsset) => {
            disableSwapIconColorMaps.set(asset.name, {
                sourceData: cloneDeep(asset.sourceData),
                colorMaps: asset.saveEditable().colorMaps,
            });
        });

        // load the scene design for the new aspect ratio
        const oldResolution = getReferenceResolution(this._aspectRatio);
        const newResolution = getReferenceResolution(value);
        this._aspectRatio = value;
        const videoData = this.root._getSceneDesignConfig(this.sceneDesign, this._aspectRatio);
        // make a copy so we don't mutate the scene design config
        const data = cloneDeep(videoData);

        // add sticker assets, they are not part of the scene design
        // reposition so they are in the same relative position
        const stickerAssetData = this.getAssets()
            .filter(Config.isStickerAsset)
            .filter((asset) => !asset.isMatchCutDuplicate)
            .map((asset) => {
                const assetData = asset.save();

                // Recalculate new position
                const bounds = asset.approximateMaxBounds;
                const oldCenterX = bounds.x + bounds.width * 0.5;
                const oldCenterY = bounds.y + bounds.height * 0.5;
                const relativeX = oldCenterX / oldResolution.width;
                const relativeY = oldCenterY / oldResolution.height;
                const newX = relativeX * newResolution.width;
                const newY = relativeY * newResolution.height;
                assetData.transformation.position.x = newX;
                assetData.transformation.position.y = newY;

                // Stickers in an AssetGroup will not have center anchor so apply it here
                assetData.transformation.anchor.x = 0.5;
                assetData.transformation.anchor.y = 0.5;

                if (Config.isChartAsset(asset)) {
                    const CHART_VIEWPORT_GAP = 20;
                    assetData.maxWidth = newResolution.width - 2 * CHART_VIEWPORT_GAP;
                    assetData.maxHeight = newResolution.height - 2 * CHART_VIEWPORT_GAP;
                }

                return assetData;
            });
        data.stickers = stickerAssetData;

        this.load(data);

        // re-apply asset changes
        this.loadEditable(editableProperties);

        // Restore colorMaps for DisableSwap icon assets if sourceData is unchanged
        (
            this.getAssets({ deep: true }).filter(
                (asset) => asset.hasSuffix(AssetSuffix.DisableSwap) && Config.isIconAsset(asset),
            ) as IconAsset[]
        ).forEach((asset: IconAsset) => {
            const savedData = disableSwapIconColorMaps.get(asset.name);
            if (savedData && isEqual(savedData.sourceData, asset.sourceData)) {
                // sourceData is the same, restore the colorMaps
                asset.loadEditable({ colorMaps: savedData.colorMaps });
            }
        });

        this.generateNewAssetIds();
    }

    /**
     * The category of the currently loaded scene. One of `Config.ALL_CATEGORIES`
     */
    set sceneCategory(value: SceneCategory) {
        if (this.#sceneCategory === value) {
            return;
        }
        assertInArray(value, Config.ALL_CATEGORIES);
        this.#sceneCategory = value;
        this.requiresUpdate = true;
    }

    get sceneCategory(): SceneCategory | null {
        return this.#sceneCategory;
    }

    /**
     * Gets part of the transcript that appears during this scene.
     *
     * If no transcript has been set on the SceneGroup,
     * this method will return an empty transcript.
     *
     * Used for Scene Annotations.
     */
    extractTranscript(): Transcript {
        return this.#requiredSceneGroup.captions.transcript.getTrimmedTranscript(
            this._calculateVoicedrivenPlaybackRegionsDuringScene(),
        );
    }

    /** @internal */
    _calculateVoicedrivenPlaybackRegionsDuringScene(): Readonly<Readonly<PlaybackRegion>[]> {
        const sceneGroupPlaybackRegions = this.#requiredSceneGroup.voicedrivenAssetPlaybackRegions;
        const sceneGroupAssetDuration = this.#requiredSceneGroup.voicedrivenAssetDuration;
        const slideStart = PlaybackRegions.playbackTimeToAssetTime(
            sceneGroupPlaybackRegions,
            sceneGroupAssetDuration as number,
            this.startTimeRelativeToSceneGroup,
        );
        const slideEnd = PlaybackRegions.playbackTimeToAssetTime(
            sceneGroupPlaybackRegions,
            sceneGroupAssetDuration as number,
            this.endTimeRelativeToSceneGroup,
        );

        // if the playback regions are not set, default to giving the full slide duration
        if (sceneGroupPlaybackRegions.length === 0) {
            return [[slideStart, slideEnd]];
        }

        const playbackRegions: Readonly<Readonly<PlaybackRegion>[]> = sceneGroupPlaybackRegions
            .filter((region) => {
                const [regionStart, regionEnd] = region;
                const regionOverlapsSlide = regionEnd >= slideStart && regionStart <= slideEnd;
                return regionOverlapsSlide;
            })
            .map((region) => {
                return [Math.max(region[0], slideStart), Math.min(region[1], slideEnd)];
            });
        return playbackRegions;
    }

    /**
     * This method converts the provided scene's progress, which a value between zero and one, and
     * converts it into luminary's (global) time.
     * @param progress - which is a value between 0 and 1
     * @internal
     */
    _getLuminaryTimeAtSceneProgress(progress: number): number {
        return this.duration * progress + this.startTime;
    }

    /** Get the preview time for a specific subscene. This is the time at which all assets are fully visible. */
    #getPreviewRelTime(subsceneIndex: number): number {
        // default to the middle of the subscene
        const subsceneTimeRanges =
            this._subsceneManager._timeline.getRelativeTimeRangesForChildren();
        const textVisibilityStart =
            this._subsceneManager._timeline.getProgressMidPointForChildren()[subsceneIndex];
        const textVisibilityEnd = subsceneTimeRanges[subsceneIndex][1];

        // we only support one fancy caption asset per scene which is why we can safely assume that the
        // first fancy caption asset is the one we want
        const fancyCaptionAssets = this.getAssetsByType(Config.FANCY_CAPTION_ASSET_TYPE, {
            deep: true,
        });
        let preferred = textVisibilityStart;
        if (fancyCaptionAssets.length > 0) {
            // get the chunk end time that is closest to the text visibility start time and doesn't exceed the text visibility end time
            const chunkEndTimes = fancyCaptionAssets[0].getPreviewRelTimes();
            const index = chunkEndTimes.findIndex(
                (time) => time >= textVisibilityStart && time <= textVisibilityEnd,
            );
            if (index !== -1) {
                preferred = chunkEndTimes[index];
            }
        }

        return preferred;
    }

    /** @internal */
    _getSubsceneIndex(asset: Asset): number {
        return this.subscenes.findIndex((subscene) => subscene.getAssets().includes(asset));
    }

    /**
     * Returns the global luminary timestamp for generating a scene preview where all assets are fully visible.
     * Specify subsceneIndex to get preview time for a specific subscene.
     */
    getPreviewTime({ subsceneIndex } = { subsceneIndex: 0 }): number {
        const relTime = this.#getPreviewRelTime(subsceneIndex);
        return this._getLuminaryTimeAtSceneProgress(relTime);
    }

    /**
     * Renders a preview for the scene to the provided canvas. By default, it will generate the
     * preview at the midpoint of the first subscene (unless subsceneIndex is provided). However,
     * the subscene midpoint is superseded if the scene has a fancy caption. In that case, the
     * preview will be generated at a chunk boundary of the fancy caption.
     */
    async renderPreviewToCanvas(
        canvas: HTMLCanvasElement,
        {
            subsceneIndex,
            abortSignal,
            mode,
        }: { subsceneIndex: number; abortSignal?: AbortSignal; mode?: SceneImageRenderMode } = {
            subsceneIndex: 0,
        },
    ) {
        const relTime = this.#getPreviewRelTime(subsceneIndex);

        return this.root._createRenderSceneImageTask({
            sceneId: this.id,
            subsceneIndex,
            relTime,
            targets: [canvas],
            abortSignal,
            mode: mode ?? DEFAULT_SCENE_IMAGE_RENDER_MODE,
        });
    }

    /**
     * State of scene has been changed, so we notify subscribers about it, they will trigger render in case they need fresh preview
     * @param subsceneIndex - The subscene index we want to render an image for. A value of -1 will render an image each subscene.
     * @internal
     */
    _emitViewChanged(subsceneIndex = -1) {
        if (subsceneIndex === -1) {
            this.subscenes.forEach((s, subsceneIndex) => {
                this.root.emit('viewChanged', { sceneId: this.id, subsceneIndex });
            });
        } else {
            this.root.emit('viewChanged', { sceneId: this.id, subsceneIndex });
        }
    }

    /** @internal */
    _updateAllTemplateReferencedAssets() {
        const initAutoDraw = this.autoDraw;
        this.autoDraw = false;

        this.getAssets().forEach((child: Asset) => {
            const shouldChildRedraw = child._hasTemplateReferenceValues();
            if (shouldChildRedraw) {
                // we don't have the update our dynamic styling setup. We simply need to re-get its values
                child.requiresRedraw = true;
            }
            if (child.getFilters()) {
                child.getFilters().forEach((filter) => {
                    if (getAllTemplateAttributes(filter).length > 0) {
                        filter.requiresUpdate = true;
                    }
                });
            }
        });

        this.autoDraw = initAutoDraw;
    }

    static #removeHelpers(helperScene: PIXI.Container) {
        while (helperScene.children.length !== 0) {
            (helperScene.children[0].destroy as (options?: boolean) => void)(true);
        }
    }

    get displayWatermarkGuides() {
        return this.#displayWatermarkGuides;
    }

    set displayWatermarkGuides(state: boolean) {
        if (state === this.#displayWatermarkGuides) {
            return;
        }

        this.#displayWatermarkGuides = state;
        this.requiresUpdate = true;
    }

    static #updateHelpers(
        helperScene: PIXI.Container,
        assetContainers: BaseGroup[],
        watermarkGuides: ConstructorParameters<typeof WatermarkBoundsDisplay>[0] | null,
    ) {
        Scene.#removeHelpers(helperScene);

        assetContainers
            .map((child) => child._getAllBaseAssetsWithDisplayedHelpers())
            .flat()
            .forEach((child) => {
                child.createHelpers().forEach((helper) => {
                    helperScene.addChild(helper);
                });
            });

        if (!watermarkGuides) {
            return;
        }
        const watermarkHelper = new WatermarkBoundsDisplay(watermarkGuides);
        helperScene.addChild(watermarkHelper);
        watermarkHelper.requiresUpdate = true;
    }

    #animateHelpers() {
        this._helperScene.children.forEach((helper) => {
            (helper as PIXI.DisplayObject & { requiresUpdate?: boolean }).requiresUpdate = true;
        });
    }

    set autoDraw(value: boolean) {
        if (this.autoDraw === value) {
            return;
        }
        this._autoDraw = value;
        this.sceneWorkspace.autoDraw = value;
    }

    get autoDraw(): boolean {
        return this._autoDraw;
    }

    /** @internal */
    _seekAssetTimelinesToAnimationMidpoint(subsceneIndex: number) {
        this.getAssets({ deep: true }).forEach((asset) => {
            // do not seek assets that are not in the given subscene
            if (asset.luminaryParent instanceof Subscene) {
                if (asset.luminaryParent.index !== subsceneIndex) {
                    return;
                }
            }

            if (asset.hasMorphAnimation || asset.hasMorphLeadInAnimation) {
                return;
            }

            const introTime = asset.appliedIntroAnimationOffset;
            const outroTime = asset.container.duration - asset.appliedOutroAnimationOffset;

            const midpoint = (introTime + outroTime) / 2;

            const progress = Math.max(0, Math.min(1, midpoint / asset.container.duration));
            asset.seek(progress, SeekMode.ResolveWhenSingleFrame);
        });
    }

    seek(options: SceneAnimateOptions, seekMode: SeekMode) {
        const progress = this._timeline.progress;
        if (options.transition) {
            this.#requiredTransitionManager.seek(progress, seekMode);
        }
        if (options.children) {
            this.scene.seek(progress, seekMode);
            this.stickers.seek(progress, seekMode);
            this._subsceneManager.seek(progress, seekMode);
            this.#animateHelpers();
        }
    }

    insertAsset(child: BaseAsset) {
        this.scene.insertAsset(child);
    }

    insertAssetAt(child: BaseAsset, index: number) {
        this.scene.insertAssetAt(child, index);
    }

    moveChild(child: BaseAsset, index: number) {
        this.scene.moveChild(child, index);
    }

    /**
     * Returns true if any of the slide's children still require to be drawn
     */
    get hasPendingRedraw(): boolean {
        if (this.destroyed) {
            return false;
        }

        if (!this.areAssetsDoneTryingToLoad) {
            return true;
        }

        if (
            this.scene._recursiveRequiresRedraw ||
            this.stickers._recursiveRequiresRedraw ||
            this._subsceneManager._recursiveRequiresRedraw ||
            this.#requiredTransitionManager._recursiveRequiresRedraw
        ) {
            return true;
        }
        return false;
    }

    #emitChildUpdate() {
        if (this.autoDraw) {
            this.emit('childUpdated');
        }
    }

    /**
     * Determines whether our not this component is active at the currentTime. Active means that the component will be
     * evaluated and visible when rendering.
     */
    active(): boolean {
        return this._timeline.active;
    }

    override get autoUpdate() {
        return super.autoUpdate;
    }

    override set autoUpdate(value) {
        if (this.autoUpdate === value) {
            return;
        }
        this._getLifecycleAwareObjects().forEach((r) => {
            if (r instanceof TimeAwareBase) {
                r.autoUpdate = value;
            }
        });
        super.autoUpdate = value;
    }

    override get editableProperties() {
        return super.editableProperties.concat([
            'displayCaptionsOverride',
            'attribution',
            'watermark',
        ]);
    }

    override get propertiesToSerialize() {
        return [
            'sceneDesign',
            'sceneCategory',
            'textPositionPresets',
            'customTransition',
            'transitionEnabled',
            'morphEnabled',
            'sfx',
            'isPrePackagedMorphWithNextScene',
            'matchCutTechnique',
        ];
    }

    override get readonlyProperties() {
        // Scene serializes some of its read-only properties
        return ['version', 'id', 'aspectRatio'];
    }

    /**
     * Get all editable properties of all editable assets
     */
    override saveEditable() {
        // collect editable properties on the slide level
        const properties = super.saveEditable();

        // collect editable properties from each component on the slide level
        const slide = this.scene
            .getAssets()
            .filter((item) => item.isEditable)
            .filter((item) => !item.hasSuffix(AssetSuffix.DisableSwap))
            .map((item) => item.saveEditable());

        // collect editable properties from each component on the subscene level
        const subslides = this.subscenes.map((subslide) => subslide.saveEditable());

        return Object.assign(properties, { slide, subslides });
    }

    /**
     * Apply the sourceMap to our current scene hierarchy.
     */
    override loadEditable(
        sourceMap: Record<string, unknown> & {
            slide?: Record<string, unknown>[];
            subslides?: { duration: number; children: Record<string, unknown>[] }[][];
            attribution?: string | null;
        } = {},
    ) {
        assertHasOwnProperties(sourceMap, ['slide', 'subslides', 'attribution']);

        // load slide level properties
        super.loadEditable(sourceMap);

        const slide = sourceMap.slide!;
        const subslides = sourceMap.subslides!;

        if (slide.length === 0 && subslides.length === 0) {
            return;
        }

        // load slide child asset properties
        slide.forEach((data) => {
            // TODO: I'm not sure if this is correct. Maybe some properties are deeper than assets.
            // such as in normalStyle?
            const name = data.name as string;
            if (!this.hasAssetByName(name, { deep: true })) {
                return;
            }
            const child = this.getAssetByName(name);
            if (child!.isEditable) {
                child!.loadEditable(data);
            }
        });

        // sync up the number of subslides
        if (subslides.length !== this.subscenes.length) {
            // add any missing subslides
            if (subslides.length > this.subscenes.length) {
                for (let i = this.subscenes.length; i < subslides.length; i++) {
                    this.addSubscene();
                }
            }
            // remove extra subslides
            else {
                for (let i = subslides.length; i < this.subscenes.length; i++) {
                    this.removeSubslide(this.subscenes.length - 1);
                }
            }
        }
        // load subslide asset properties
        subslides.forEach((subslide, index) => {
            this.subscenes[index].duration = subslide[0].duration;
            subslide[0].children.forEach((data) => {
                const name = data.name as string;
                if (!this.subscenes[index].hasAssetByName(name, { deep: true })) {
                    return;
                }
                this.subscenes[index].getAssetByName(name)!.loadEditable(data);
            });
        });
        return this;
    }

    canMorph(): boolean {
        const nextScene = this.nextScene;
        if (!nextScene) {
            return false;
        }

        const nextSceneAssets = nextScene.getAssets({ deep: true });

        return this.getAssets({ deep: true }).some((thisAsset) =>
            nextSceneAssets.some((nextAsset) => thisAsset.isMorphMatch(nextAsset)),
        );
    }

    clearMorphAnimations() {
        this.#morphManager!.clearMorphAnimations();
    }

    /** @internal */
    _resetMorphAnimations() {
        this.#morphManager!.resetMorphAnimations();
    }

    /** @internal */
    get _logInfo() {
        return `Scene, ${JSON.stringify({
            sceneDesignId: this.sceneDesign,
        })}`;
    }

    /**
     * Returns the list of sound effects attached to this scene.
     */
    get sfx(): readonly SceneSfxItem[] {
        return this.#sfx;
    }

    /**
     * Add a sound effect to this scene.
     * @param settings - Settings for the SFX including sourceData, event, and offset
     */
    addSfx(settings: AddSceneSfxSettings): SceneSfxItem {
        const sfx: SceneSfxItem = {
            id: uuid(),
            sourceData: settings.sourceData,
            event: settings.event,
            offset: settings.offset ?? 0,
        };
        this.#sfx = [...this.#sfx, sfx];

        this.#createSfxAudioAsset(sfx);
        this.requiresUpdate = true;

        return sfx;
    }

    /**
     * Create an AudioAsset for an SFX item.
     */
    #createSfxAudioAsset(sfx: SceneSfxItem): void {
        const audioAsset = new AudioAsset({
            fetchResources: this.fetchResources,
            name: `sfx_${sfx.id}`,
            parent: this,
            autoUpdate: true,
            id: sfx.id,
            getAudioContext: () => this.root.audioContext,
            getIsExternallyMuted: () => this.root.muted,
        });

        audioAsset.sourceData = sfx.sourceData;
        audioAsset.loop = false;

        this.#sfxAudioAssets.set(sfx.id, audioAsset);
    }

    /**
     * Remove a sound effect from this scene by its ID.
     * @param id - The ID of the SFX to remove
     */
    removeSfx(id: string): void {
        const initialLength = this.#sfx.length;
        this.#sfx = this.#sfx.filter((s) => s.id !== id);
        if (this.#sfx.length !== initialLength) {
            const audioAsset = this.#sfxAudioAssets.get(id);
            if (audioAsset) {
                audioAsset._propagatePause();
                audioAsset.destroy();
                this.#sfxAudioAssets.delete(id);
            }
            this.requiresUpdate = true;
        }
    }

    /**
     * Get a sound effect by its ID.
     * @param id - The ID of the SFX to retrieve
     * @returns The SceneSfxItem if found, undefined otherwise
     */
    getSfxById(id: string): SceneSfxItem | undefined {
        return this.#sfx.find((s) => s.id === id);
    }

    /**
     * Get the AudioAsset for a sound effect by its ID.
     * @param id - The ID of the SFX
     * @returns The AudioAsset if found, undefined otherwise
     * @internal
     */
    _getSfxAudioAsset(id: string): AudioAsset | undefined {
        return this.#sfxAudioAssets.get(id);
    }

    /**
     * Clean up all SFX audio assets and clear the sfx.
     * Pauses and destroys all AudioAssets and clears both the map and the list.
     */
    #clearSfxAudio(): void {
        this.#sfxAudioAssets.forEach((audioAsset) => {
            audioAsset._propagatePause();
            audioAsset.destroy();
        });
        this.#sfxAudioAssets.clear();
        this.#sfx = [];
    }

    /**
     * Calculate the global playback time for a given SFX based on its event trigger.
     * @param sfx - The SFX item to calculate playback time for
     * @returns The global playback time in milliseconds
     * @internal
     */
    _getSfxPlaybackTime(sfx: SceneSfxItem): number {
        const sceneStartTime = this.startTime;
        const offset = sfx.offset ?? 0;

        switch (sfx.event) {
            case SceneSfxEvent.SCENE_START:
                return sceneStartTime + offset;
            case SceneSfxEvent.TRANSITION_IN_MIDPOINT: {
                const transitionDuration = this.prevScene
                    ? this.prevScene.endTime - this.startTime
                    : 0;
                return sceneStartTime + transitionDuration / 2 + offset;
            }
            default:
                ErrorReporter.setContext('sfx', { event: sfx.event });
                throw new Error('Invalid SFX event');
        }
    }

    /**
     * Custom load method for sfx deserialization.
     * @internal
     */
    _sfxCustomLoad(data: SceneSfxItem[] | null): void {
        if (!data || !Array.isArray(data)) {
            this.#sfx = [];
            return;
        }
        this.#sfx = data;

        this.#sfx.forEach((sfx) => {
            this.#createSfxAudioAsset(sfx);
        });
    }
}
